import { useEffect, useRef, useState } from 'react'
import { Link, Navigate, useParams } from 'react-router-dom'
import { getAdjacent, getSong } from '../data/songs'
import { StudioPlayer } from '../components/StudioPlayer'
import { mediaUrl } from '../lib/media'

export function SongPage() {
  const { slug = '' } = useParams()
  const song = getSong(slug)
  const { prev, next } = getAdjacent(slug)
  const [videoError, setVideoError] = useState(false)
  const [lang, setLang] = useState<'en' | 'fr'>('en')
  const hold = useRef<{ slug: string; time: number; paused: boolean } | null>(null)
  const videoRef = useRef<HTMLVideoElement>(null)

  useEffect(() => {
    hold.current = null
    setLang('en')
    setVideoError(false)
  }, [slug])

  if (!song) return <Navigate to="/music" replace />

  const active = song
  const bilingual = Boolean(active.videoFr && active.lyricsFr?.length)
  const french = bilingual && lang === 'fr'
  const videoSrc = mediaUrl(french && active.videoFr ? active.videoFr : active.video)
  const lines = french && active.lyricsFr ? active.lyricsFr : active.lyrics

  function choose(nextLang: 'en' | 'fr') {
    if (nextLang === lang) return
    const node = videoRef.current
    hold.current = {
      slug: active.slug,
      time: node?.currentTime || 0,
      paused: node ? node.paused : true,
    }
    setVideoError(false)
    setLang(nextLang)
  }

  return (
    <article className="song">
      <header className="song-head">
        <h1 className="song-title">{active.title}</h1>
        <p className="song-meta">AIAS · {active.year}</p>
        <p className="song-tagline">{active.tagline}</p>
      </header>

      {active.audio ? (
        <StudioPlayer
          src={active.audio}
          srcAlt={active.audioAlt}
          title={active.title}
          downloadName={active.mp3Label || `AIAS - ${active.title}.mp3`}
        />
      ) : null}

      <div className="player-shell">
        <div className="video-head">
          <p className="video-kicker">Music video</p>
          {bilingual ? (
            <div className="lang-switch" role="group" aria-label="Caption language">
              <button type="button" aria-pressed={lang === 'en'} onClick={() => choose('en')}>
                English
              </button>
              <button type="button" aria-pressed={lang === 'fr'} onClick={() => choose('fr')}>
                Français
              </button>
            </div>
          ) : null}
        </div>
        {!videoError ? (
          <video
            ref={videoRef}
            key={`${active.slug}-${french ? 'fr' : 'en'}`}
            controls
            playsInline
            preload="auto"
            muted={false}
            poster={active.cover ? mediaUrl(active.cover) : undefined}
            onError={() => setVideoError(true)}
            onLoadedMetadata={(event) => {
              const saved = hold.current
              if (!saved || saved.slug !== active.slug) return
              const node = event.currentTarget
              try {
                if (saved.time > 0 && saved.time < (node.duration || saved.time + 1)) {
                  node.currentTime = saved.time
                }
              } catch {
                /* the new film may be shorter than the saved place */
              }
              if (!saved.paused) {
                node.muted = false
                node.play().catch(() => {})
              }
            }}
            onPlay={(event) => {
              const node = event.currentTarget
              node.muted = false
              try {
                node.volume = 1
              } catch {
                /* ignore */
              }
            }}
          >
            <source src={videoSrc} type="video/mp4" />
          </video>
        ) : (
          <div className="player-fallback">
            <div>
              <strong>Video not linked yet</strong>
              Try the Studio stream / Download MP3 above.
            </div>
          </div>
        )}
      </div>

      <div className="song-grid">
        <section className="lyrics-panel" aria-label={french ? 'Paroles' : 'Lyrics'}>
          <h2>{french ? 'Paroles' : 'Lyrics'}</h2>
          <div className="lyrics">
            {lines.map((line, i) =>
              line === '' ? (
                <p key={i} className="blank" />
              ) : (
                <p key={i}>{line}</p>
              ),
            )}
          </div>
        </section>

        {active.audio ? (
          <aside className="download-panel" aria-label="Downloads">
            <h2>Download</h2>
            <a
              className="btn btn-solid"
              style={{ width: '100%', display: 'block', textAlign: 'center' }}
              href={mediaUrl(active.audio)}
              download={active.mp3Label || `AIAS - ${active.title}.mp3`}
            >
              Download MP3
            </a>
            {active.audioAlt ? (
              <a
                className="btn"
                style={{ width: '100%', display: 'block', textAlign: 'center', marginTop: '0.75rem' }}
                href={mediaUrl(active.audioAlt)}
                download={(active.mp3Label || `AIAS - ${active.title}.mp3`).replace(/\.mp3$/i, '.m4a')}
              >
                Download M4A
              </a>
            ) : null}
          </aside>
        ) : null}
      </div>

      <nav className="song-nav" aria-label="Adjacent songs">
        {prev ? (
          <Link to={`/music/${prev.slug}`}>
            Previous
            <span>{prev.title}</span>
          </Link>
        ) : (
          <span />
        )}
        {next ? (
          <Link to={`/music/${next.slug}`} style={{ textAlign: 'right' }}>
            Next
            <span>{next.title}</span>
          </Link>
        ) : (
          <span />
        )}
      </nav>
    </article>
  )
}
